import React, { useCallback, useRef, useMemo } from 'react';
import { TouchableOpacity, Text, ActivityIndicator, View } from 'react-native';
import { DbButtonProps, ButtonIconConfig } from './DbButton.types';
import { baseStyles, getDynamicStyles, getIconSize, getIconColor } from './DbButton.styles';
import DbIcon from '../DbIcon';
import { useDbTheme } from '@/core';

/**
 * 防抖函数
 */
function useDebounce<T extends (...args: any[]) => void>(
    callback: T | undefined,
    delay: number
): T {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const debouncedCallback = useCallback(
        (...args: Parameters<T>) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                callback?.(...args);
            }, delay);
        },
        [callback, delay]
    ) as T;

    return debouncedCallback;
}

/**
 * 节流函数
 */
function useThrottle<T extends (...args: any[]) => void>(
    callback: T | undefined,
    delay: number
): T {
    const lastCallRef = useRef<number>(0);
    const isThrottledRef = useRef<boolean>(false);

    const throttledCallback = useCallback(
        (...args: Parameters<T>) => {
            const now = Date.now();
            if (now - lastCallRef.current >= delay && !isThrottledRef.current) {
                lastCallRef.current = now;
                isThrottledRef.current = true;
                callback?.(...args);
                setTimeout(() => {
                    isThrottledRef.current = false;
                }, delay);
            }
        },
        [callback, delay]
    ) as T;

    return throttledCallback;
}

export const DbButton: React.FC<DbButtonProps> = ({
    title,
    onPress,
    loading = false,
    disabled = false,
    variant = 'primary',
    size = 'medium',
    style,
    textStyle,
    // 图标配置
    icon,
    leftIcon,
    rightIcon,
    // 防抖节流
    debounce,
    throttle,
    // 其他
    iconOnly = false,
    loadingText,
    ...props
}) => {
    const theme = useDbTheme();
    const isDisabled = disabled || loading;

    // 获取动态样式（使用主题颜色）
    const dynamicStyles = useMemo(
        () => getDynamicStyles(theme.colors.primary),
        [theme.colors.primary]
    );

    // 处理防抖
    const debouncedPress = useDebounce(onPress, debounce || 0);
    
    // 处理节流
    const throttledPress = useThrottle(onPress, throttle || 0);

    // 最终的点击处理函数
    const handlePress = useCallback(() => {
        if (debounce && debounce > 0) {
            debouncedPress();
        } else if (throttle && throttle > 0) {
            throttledPress();
        } else {
            onPress?.();
        }
    }, [debounce, throttle, debouncedPress, throttledPress, onPress]);

    // 获取默认图标大小和颜色
    const defaultIconSize = useMemo(() => getIconSize(size), [size]);
    const defaultIconColor = useMemo(
        () => getIconColor(variant, theme.colors.primary),
        [variant, theme.colors.primary]
    );

    // 根据变体获取 ActivityIndicator 颜色
    const activityIndicatorColor = useMemo(() => {
        if (variant === 'outline' || variant === 'ghost') {
            return theme.colors.primary;
        }
        return '#FFFFFF';
    }, [variant, theme.colors.primary]);

    // 渲染图标
    const renderIcon = (
        iconConfig: ButtonIconConfig | React.ReactNode | undefined,
        position: 'left' | 'right'
    ) => {
        if (!iconConfig) return null;

        // 如果是 ReactNode
        if (React.isValidElement(iconConfig)) {
            return (
                <View style={[
                    baseStyles.iconContainer,
                    position === 'left' ? baseStyles.iconLeft : baseStyles.iconRight
                ]}>
                    {iconConfig}
                </View>
            );
        }

        // 如果是 IconConfig
        const config = iconConfig as ButtonIconConfig;
        const spacing = config.spacing ?? 6;

        return (
            <View style={[
                baseStyles.iconContainer,
                position === 'left' 
                    ? { marginRight: !iconOnly && title ? spacing : 0 } 
                    : { marginLeft: !iconOnly && title ? spacing : 0 }
            ]}>
                <DbIcon
                    name={config.name}
                    family={config.family || 'Ionicons'}
                    size={config.size || defaultIconSize}
                    color={config.color || defaultIconColor}
                />
            </View>
        );
    };

    // 解析 icon 属性，确定左右图标
    const resolvedLeftIcon = useMemo(() => {
        if (leftIcon) return leftIcon;
        if (icon) {
            if (React.isValidElement(icon)) return icon;
            const config = icon as ButtonIconConfig;
            if (!config.position || config.position === 'left') return icon;
        }
        return undefined;
    }, [leftIcon, icon]);

    const resolvedRightIcon = useMemo(() => {
        if (rightIcon) return rightIcon;
        if (icon) {
            if (React.isValidElement(icon)) return undefined;
            const config = icon as ButtonIconConfig;
            if (config.position === 'right') return icon;
        }
        return undefined;
    }, [rightIcon, icon]);

    // 获取文字尺寸样式
    const textSizeStyle = useMemo(() => {
        switch (size) {
            case 'small':
                return baseStyles.smallText;
            case 'large':
                return baseStyles.largeText;
            default:
                return baseStyles.mediumText;
        }
    }, [size]);

    // 获取变体对应的文字颜色样式
    const textColorStyle = useMemo(() => {
        switch (variant) {
            case 'primary':
                return dynamicStyles.primaryText;
            case 'secondary':
                return dynamicStyles.secondaryText;
            case 'outline':
                return dynamicStyles.outlineText;
            case 'ghost':
                return dynamicStyles.ghostText;
            default:
                return dynamicStyles.primaryText;
        }
    }, [variant, dynamicStyles]);

    // 获取变体对应的背景样式
    const variantStyle = useMemo(() => {
        switch (variant) {
            case 'primary':
                return dynamicStyles.primary;
            case 'secondary':
                return dynamicStyles.secondary;
            case 'outline':
                return dynamicStyles.outline;
            case 'ghost':
                return dynamicStyles.ghost;
            default:
                return dynamicStyles.primary;
        }
    }, [variant, dynamicStyles]);

    return (
        <TouchableOpacity
            style={[
                baseStyles.container,
                variantStyle,
                baseStyles[size],
                isDisabled && baseStyles.disabled,
                style,
            ]}
            onPress={handlePress}
            disabled={isDisabled}
            activeOpacity={theme.interaction.activeOpacity}
            {...props}>
            {loading ? (
                <View style={baseStyles.contentContainer}>
                    <ActivityIndicator
                        color={activityIndicatorColor}
                        size="small"
                    />
                    {loadingText && (
                        <Text style={[
                            baseStyles.text,
                            textColorStyle,
                            textSizeStyle,
                            { marginLeft: 8 },
                            textStyle
                        ]}>
                            {loadingText}
                        </Text>
                    )}
                </View>
            ) : (
                <View style={baseStyles.contentContainer}>
                    {renderIcon(resolvedLeftIcon, 'left')}
                    {!iconOnly && title && (
                        <Text style={[
                            baseStyles.text,
                            textColorStyle,
                            textSizeStyle,
                            textStyle
                        ]}>
                            {title}
                        </Text>
                    )}
                    {renderIcon(resolvedRightIcon, 'right')}
                </View>
            )}
        </TouchableOpacity>
    );
};

// 向后兼容别名
export const Button = DbButton;

export default DbButton;
